import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, throwError, timer, retry } from 'rxjs';
import { ToastService } from '@core/services/toast.service';

/**
 * Configuration du rate limiting côté client
 */
const RATE_LIMIT_CONFIG = {
  maxRetries: 3,
  initialDelayMs: 1000,
  maxDelayMs: 30000,
  showToast: true,
};

/**
 * Intercepteur pour gérer les erreurs de rate limiting (429 Too Many Requests)
 * - Affiche un message à l'utilisateur
 * - Implémente un retry avec backoff exponentiel
 * - Suit le header Retry-After si présent
 */
export const rateLimitInterceptor: HttpInterceptorFn = (req, next) => {
  const toastService = inject(ToastService);

  return next(req).pipe(
    retry({
      count: RATE_LIMIT_CONFIG.maxRetries,
      delay: (error: HttpErrorResponse, retryCount: number) => {
        if (error.status === 429) {
          // Récupérer le délai depuis le header Retry-After
          const retryAfter = error.headers
            ? error.headers.get('Retry-After')
            : null;
          let delayMs = RATE_LIMIT_CONFIG.initialDelayMs;

          if (retryAfter) {
            // Retry-After peut être en secondes ou une date
            const retrySeconds = parseInt(retryAfter, 10);
            if (!isNaN(retrySeconds)) {
              delayMs = retrySeconds * 1000;
            }
          } else {
            // Backoff exponentiel
            delayMs = Math.min(
              RATE_LIMIT_CONFIG.initialDelayMs * Math.pow(2, retryCount - 1),
              RATE_LIMIT_CONFIG.maxDelayMs,
            );
          }

          // Afficher un toast si configuré
          if (RATE_LIMIT_CONFIG.showToast && retryCount === 1) {
            const seconds = Math.ceil(delayMs / 1000);
            toastService.warning(
              `Trop de requêtes. Veuillez patienter ${seconds} secondes.`,
              'Limite atteinte',
            );
          }

          // Si on a atteint le max de retries
          if (retryCount >= RATE_LIMIT_CONFIG.maxRetries) {
            toastService.error(
              'Le service est temporairement surchargé. Veuillez réessayer plus tard.',
              'Service indisponible',
            );
            return throwError(() => error);
          }

          // Réessayer après le délai
          return timer(delayMs);
        }

        return throwError(() => error);
      },
    }),
  );
};

/**
 * Intercepteur pour ajouter des headers de sécurité
 */
export const securityHeadersInterceptor: HttpInterceptorFn = (req, next) => {
  // Ajouter des headers de sécurité
  const secureReq = req.clone({
    setHeaders: {
      'X-Content-Type-Options': 'nosniff',
      'X-Frame-Options': 'DENY',
      'X-XSS-Protection': '1; mode=block',
    },
  });

  return next(secureReq);
};

/**
 * Intercepteur pour tracker les requêtes (utile pour afficher un loader)
 */
export const requestTrackerInterceptor: HttpInterceptorFn = (req, next) => {
  // On pourrait utiliser un signal ou un service pour tracker les requêtes en cours
  const startTime = Date.now();

  return next(req).pipe(
    catchError((error) => {
      const duration = Date.now() - startTime;
      console.debug(
        `[Request Failed] ${req.method} ${req.url} - ${duration}ms`,
        error,
      );
      return throwError(() => error);
    }),
  );
};
